/**
 * SellerDashboard - Dashboard for sellers to manage listings and view earnings
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { db, getArtifactProjectId } from '../../firebaseInit';
import { useAuth } from '../../contexts/AuthContext';
import { useMarketplace } from '../../contexts/MarketplaceContext';
import Card from '../shared/Card';
import Button from '../shared/Button';
import Modal from '../shared/Modal';
import LoadingSpinner from '../shared/LoadingSpinner';
import EmptyState from '../shared/EmptyState';
import { getStatusBadgeStyles, getCategoryById } from './MarketplaceCategories';

const SellerDashboard = () => {
  const navigate = useNavigate();
  const { userTeam, isAdmin, isTeamLead } = useAuth();
  const { 
    myListings, 
    myListingsLoading, 
    hasMarketplaceAccess, 
    categories,
    initializeMarketplace,
    loadMyListings,
    deleteListing 
  } = useMarketplace();

  // Initialize marketplace data on mount
  useEffect(() => {
    initializeMarketplace();
  }, [initializeMarketplace]);

  const [stripeConnected, setStripeConnected] = useState(false);
  const [loadingStripe, setLoadingStripe] = useState(true);
  const [earnings, setEarnings] = useState({ total: 0, pending: 0, available: 0 });
  const [deleting, setDeleting] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(null);

  // Check Stripe Connect status
  useEffect(() => {
    const checkStripeStatus = async () => {
      try {
        const appId = getArtifactProjectId();
        const teamRef = doc(db, `artifacts/${appId}/teams`, userTeam);
        const teamSnap = await getDoc(teamRef);
        
        if (teamSnap.exists()) {
          const teamData = teamSnap.data();
          setStripeConnected(!!teamData.stripeConnectAccountId);
          
          // Mock earnings for now - would come from Cloud Function
          setEarnings({
            total: 0,
            pending: 0,
            available: 0
          });
        }
      } catch (err) {
        console.error('Error checking Stripe status:', err);
      } finally {
        setLoadingStripe(false);
      }
    };

    if (userTeam) {
      checkStripeStatus();
    }
  }, [userTeam]);

  // Access check
  if (!hasMarketplaceAccess) {
    return (
      <div className="p-4">
        <EmptyState
          icon="🔒"
          title="Access Restricted"
          description="The Script Marketplace is available to Team Leads and Admins only."
          action={
            <Button variant="primary" onClick={() => navigate('/')}>
              Go to Dashboard
            </Button>
          }
        />
      </div>
    );
  }

  const handleDeleteListing = async (listingId) => {
    setDeleting(listingId);
    try {
      await deleteListing(listingId);
      toast.success('Listing deleted successfully.');
      setShowDeleteModal(null);
    } catch (err) {
      console.error('Error deleting listing:', err);
      toast.error('Failed to delete listing. Please try again.');
    } finally {
      setDeleting(null);
    }
  };

  const handleConnectStripe = async () => {
    toast('Stripe Connect onboarding coming soon! For now, you can list free scripts.', { icon: 'ℹ️' });
  };

  // Calculate stats
  const totalSales = myListings.reduce((sum, l) => sum + (l.totalSales || 0), 0);
  const activeListings = myListings.filter(l => l.status === 'active').length;
  const pendingListings = myListings.filter(l => l.status === 'pending_review').length;

  return (
    <div className="w-full">
      {/* Sub-header */}
      <div className="flex items-center justify-between mb-6">
        <p className="text-gray-600 text-sm">
          Manage your listings and track earnings
        </p>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card padding="md">
          <p className="text-sm text-gray-500">Total Listings</p>
          <p className="text-2xl font-bold text-gray-900">{myListings.length}</p>
        </Card>
        <Card padding="md">
          <p className="text-sm text-gray-500">Active</p>
          <p className="text-2xl font-bold text-green-600">{activeListings}</p>
        </Card>
        <Card padding="md">
          <p className="text-sm text-gray-500">Pending Review</p>
          <p className="text-2xl font-bold text-yellow-600">{pendingListings}</p>
        </Card>
        <Card padding="md">
          <p className="text-sm text-gray-500">Total Sales</p>
          <p className="text-2xl font-bold text-blue-600">{totalSales}</p>
        </Card>
      </div>

      {/* Stripe Connect status */}
      <Card padding="md" className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">Payment Setup</h3>
            {loadingStripe ? (
              <p className="text-sm text-gray-500">Checking payment status...</p>
            ) : stripeConnected ? (
              <p className="text-sm text-green-600">✓ Stripe account connected</p>
            ) : (
              <p className="text-sm text-gray-500">
                Connect your Stripe account to receive payments for paid scripts
              </p>
            )}
          </div>
          {!loadingStripe && !stripeConnected && (
            <Button variant="outline" disabled title="Stripe integration is coming soon. For now, you can list free scripts.">
              Connect Stripe (Coming Soon)
            </Button>
          )}
        </div>
        
        {stripeConnected && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-sm text-gray-500">Total Earned</p>
                <p className="text-xl font-bold text-gray-900">${earnings.total.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Pending</p>
                <p className="text-xl font-bold text-yellow-600">${earnings.pending.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Available</p>
                <p className="text-xl font-bold text-green-600">${earnings.available.toFixed(2)}</p>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Listings */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Your Listings</h2>
          <Button variant="ghost" size="sm" onClick={() => loadMyListings()}>
            Refresh
          </Button>
        </div>

        {/* Loading state */}
        {myListingsLoading && myListings.length === 0 && (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="large" message="Loading listings..." />
          </div>
        )}

        {/* Empty state */}
        {!myListingsLoading && myListings.length === 0 && (
          <EmptyState
            icon="📋"
            title="No listings yet"
            description="Share your scripts with the TreeGenie community and start earning!"
            action={
              <Button variant="primary" onClick={() => navigate('/marketplace/sell')}>
                List Your First Script
              </Button>
            }
          />
        )}

        {/* Listings table */}
        {myListings.length > 0 && (
          <Card padding="none">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Script
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Price
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Sales
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Rating
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {myListings.map(listing => {
                    const statusBadge = getStatusBadgeStyles(listing.status);
                    const category = getCategoryById(listing.category, categories);
                    
                    return (
                      <tr key={listing.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <span className="text-xl">{category.icon}</span>
                            <div>
                              <p className="font-medium text-gray-900 truncate max-w-xs" title={listing.title}>
                                {listing.title}
                              </p>
                              <p className="text-xs text-gray-500">{category.name}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge.bg} ${statusBadge.text}`}>
                            {statusBadge.label}
                          </span>
                          {listing.status === 'rejected' && listing.rejectionReason && (
                            <p className="text-xs text-red-600 mt-1 max-w-xs truncate" title={listing.rejectionReason}>
                              {listing.rejectionReason}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {listing.isFree ? (
                            <span className="text-green-600 font-medium">Free</span>
                          ) : (
                            <span className="text-gray-900 font-medium">${listing.price}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-900">
                          {(listing.totalSales || 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-3">
                          {listing.reviewCount > 0 ? (
                            <div className="flex items-center gap-1">
                              <span className="text-yellow-500">★</span>
                              <span className="text-gray-900">{listing.rating?.toFixed(1)}</span>
                              <span className="text-gray-400 text-xs">({listing.reviewCount})</span>
                            </div>
                          ) : (
                            <span className="text-gray-400 text-sm">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/marketplace/listing/${listing.id}`)}
                            >
                              View
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/marketplace/sell?edit=${listing.id}`)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setShowDeleteModal(listing)}
                              disabled={deleting === listing.id}
                              className="text-red-600 hover:text-red-700"
                            >
                              {deleting === listing.id ? '...' : 'Delete'}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <Modal
          isOpen
          onClose={() => setShowDeleteModal(null)}
          title="Delete Listing"
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Are you sure you want to delete <strong>{showDeleteModal.title}</strong>? This cannot be undone.
            </p>
            <div className="flex justify-end gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowDeleteModal(null)}
                disabled={deleting === showDeleteModal.id}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => handleDeleteListing(showDeleteModal.id)}
                disabled={deleting === showDeleteModal.id}
              >
                {deleting === showDeleteModal.id ? 'Deleting...' : 'Delete Listing'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default SellerDashboard;
